export interface UserAgentInfo {
  ua: string
  isIos: boolean
  isWebkit: boolean
  isIosSafari: boolean
}

export interface IOSVersionInfo {
  isIos: boolean
  version: number | null
  versionHidden: boolean
  ua: string
  platform: string
}

interface SafariVersionInfo {
  version: number | null
  minor: number | null
}

export function detectUserAgent(): UserAgentInfo {
  const ua = window.navigator.userAgent
  const isIos = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i)
  const isWebkit = !!ua.match(/WebKit/i)
  const isIosSafari = isIos && isWebkit && !ua.match(/CriOS/i)
  return {
    ua,
    isIos,
    isWebkit,
    isIosSafari,
  }
}

// ios 26 版本的user agent範例
// Mozilla/5.0 (iPhone; CPU iPhone OS 18_6_2 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/26.0.1 Mobile/15E148 Safari/604.1

/**
 * 嘗試從 client 偵測 iOS 與 major 版本
 * - 若 userAgent 含有 `OS <major>_<minor>`，會回傳 major 數字
 */
export function detectIOSVersion(): IOSVersionInfo {
  const ua = window.navigator.userAgent || ''
  const platform = window.navigator.platform || ''
  // iPad on iOS13+ may report MacIntel but has touch points`
  const isTouchMac =
    platform === 'MacIntel' &&
    typeof window.navigator.maxTouchPoints === 'number' &&
    window.navigator.maxTouchPoints > 1
  const isIos = /iPad|iPhone|iPod/.test(ua) || isTouchMac

  let version: number | null = null
  // 常見 UA 形式: "OS 15_4_1" 或 "OS 15_4"
  const m = ua.match(/OS (\d+)_?(\d+)?_?(\d+)?/)
  if (m && m[1]) {
    version = parseInt(m[1], 10)
  }

  const versionHidden = isIos && version === null
  return {
    isIos,
    version, // major version number or null
    versionHidden,
    ua,
    platform,
  }
}

/** 以safari user agent 版本號判斷是否為 iOS 26.0 或 26.1 的版本 */
export function isIos26PatchVersion(): boolean {
  const version = getVersionFromSafariUA()
  if (version.version === 26 && version.minor !== null && version.minor <= 1) {
    return true
  }
  return false
}

function getVersionFromSafariUA(): SafariVersionInfo {
  const ua = window.navigator.userAgent || ''
  let version: number | null = null
  let minor: number | null = null

  const mVersion = ua.match(/Version\/(\d+)\.(\d+)/)
  if (mVersion && mVersion[1]) {
    version = parseInt(mVersion[1], 10)
    minor = mVersion[2] ? parseInt(mVersion[2], 10) : null
  }

  if (version === null) {
    const mSafari = ua.match(/Safari\/(\d+)\.(\d+)/)
    if (mSafari && mSafari[1]) {
      version = parseInt(mSafari[1], 10)
      minor = mSafari[2] ? parseInt(mSafari[2], 10) : null
    }
  }

  if (version === null) {
    const mWebKit = ua.match(/AppleWebKit\/(\d+)\.(\d+)/)
    if (mWebKit && mWebKit[1]) {
      version = parseInt(mWebKit[1], 10)
      minor = mWebKit[2] ? parseInt(mWebKit[2], 10) : null
    }
  }

  return { version, minor }
}
